<template>
	<Accessible
		v-if="isOpen"
		class="thread-browser"
		ref="panel"
		name="thread-browser"
		:tabOrder="browserTabOrder"
		trapped
		@esc="close"
	>
		<div class="thread-browser-header">
			<div class="thread-browser-title" v-t="'chat_threadbrowser_title'" />
			<div class="header-actions">
				<Button
					v-if="canCreateThread"
					class="create-btn"
					primary
					small
					@click="$emit('create-thread', { parentChatId: chatId })"
				>
					{{ $t('common_button_create') }}
				</Button>
				<div class="header-divider" />
				<CloseIcon type="regular" class="close-icon pointer" @click="close" />
			</div>
		</div>

		<div class="thread-search-wrapper">
			<Input
				v-model="searchQuery"
				:placeholder="$t('common_search')"
				:icon="SearchIcon"
				allowClear
			/>
		</div>

		<div class="tabs-wrapper">
			<Tabs
				:tabs="tabs"
				:defaultSelected="activeTab"
				@change="onTabChange"
				small
			/>
		</div>

		<Accessible
			ref="threadList"
			name="thread-list"
			class="thread-list-accessible"
			:tabOrder="threadListTabOrder"
			trapped
			focusFirstItem
		>
			<Table
				ref="table"
				:columns="columns"
				:rows="threads"
				infinite
				hideHeader
				clickable
				@row-click="openThread"
				@load-more="loadMore"
				:loadingMore="loadingMore"
				:loadingOverlay="loading"
				loadMoreTrigger="50px"
				height="100%"
				:rowHeight="77"
			>
				<template #cell-thread="{ data }">
					<div class="thread-item">
						<div class="thread-title">
							{{ data.title }}
						</div>
						<div class="thread-preview" v-if="data.latest_user_message">
							<ProfilePic :user="data.latest_user_message.user" size="16px" />
							<span class="preview-sender"
								>{{ data.latest_user_message.user?.fullname }}:</span
							>
							<span class="preview-text">{{
								resolvePreviewText(data.latest_user_message)
							}}</span>
							<span class="preview-time">
								<span class="time-dot">•</span>
								<RelativeTime :date="data.latest_user_message.created_at" />
							</span>
						</div>
						<div class="thread-preview no-messages" v-else>
							<span
								class="preview-text"
								v-t="'chat_thread_preview_no_messages'"
							/>
						</div>
					</div>
				</template>
				<template #empty>
					<div class="thread-browser-empty">
						<NoSearchResults v-if="searchQuery" :query="searchQuery" />
						<template v-else>
							<div class="empty-icon">
								<ReelIcon type="solid" />
							</div>
							<template v-if="activeTab === ALL_TAB">
								<div
									class="empty-title"
									v-t="'chat_threadbrowser_empty_all_title'"
								/>
								<div
									class="empty-description"
									v-t="'chat_threadbrowser_empty_all_description'"
								/>
								<Button
									v-if="canCreateThread"
									class="empty-create-btn"
									primary
									@click="$emit('create-thread', { parentChatId: chatId })"
								>
									{{ $t('chat_threadbrowser_empty_all_button') }}
								</Button>
							</template>
							<template v-else>
								<div
									class="empty-title"
									v-t="'chat_threadbrowser_empty_joined_title'"
								/>
								<div
									class="empty-description"
									v-t="'chat_threadbrowser_empty_joined_description'"
								/>
								<Button
									class="empty-create-btn"
									primary
									@click.stop="switchToAllTab"
								>
									{{ $t('chat_threadbrowser_empty_joined_button') }}
								</Button>
							</template>
						</template>
					</div>
				</template>
			</Table>
		</Accessible>
	</Accessible>
</template>

<script setup>
import { ref, reactive, computed, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { fetchThreadsForChat, fetchMyJoinedThreads } from '@/api/thread'
import {
	Tabs,
	Button,
	Input,
	ProfilePic,
	RelativeTime,
	Table,
	NoSearchResults,
} from '@/components/common'
import debounce from 'debounce'
import Accessible from '@/components/common/Accessible'
import { CloseIcon, ReelIcon, SearchIcon } from '@/icons'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'
import { getChat } from '@/store'
import { chatCan, CREATE_THREAD } from '@/helpers/permissions'
import { parseMessageParts, partType } from '@/components/Message/utils'

const { t } = useI18n()

defineOptions({ name: 'ThreadBrowser' })

const props = defineProps({
	chatId: {
		type: Number,
		required: true,
	},
	isOpen: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['close', 'open-thread', 'create-thread'])

function resolvePreviewText(message) {
	if (!message) return ''
	return parseMessageParts({
		...message,
		message: message.text || '',
		mentions: (message.mentions || []).map((m) => m.id),
	})
		.map(({ type, userId, user, text }) => {
			switch (type) {
				case partType.MENTION:
					if (user) return `@${user.fullname}`
					if (userId === 'everyone') return '@everyone'
					return `@User #${userId}`
				case partType.HYPERLINK:
				case partType.TEXT:
					return text
				default:
					return null
			}
		})
		.filter((part) => part)
		.join('')
}

const ALL_TAB = 'all'
const JOINED_TAB = 'joined'

const tabs = [
	{ id: ALL_TAB, label: t('chat_threadbrowser_tab_all') },
	{ id: JOINED_TAB, label: t('chat_threadbrowser_tab_joined') },
]

const activeTab = ref(ALL_TAB)
const loading = ref(false)
const loadingMore = ref(false)
const escapeId = ref(null)
const searchQuery = ref('')
const panel = ref(null)
const threadList = ref(null)
const columns = [{ field: 'thread' }]

const makeTabState = () => ({ threads: [], apiResponse: null })
const tabData = reactive({
	[ALL_TAB]: makeTabState(),
	[JOINED_TAB]: makeTabState(),
})

const threads = computed(() => tabData[activeTab.value].threads)

const canCreateThread = computed(() => {
	const chat = getChat(props.chatId)
	return chatCan(chat, CREATE_THREAD)
})

const browserTabOrder = computed(() => {
	const order = []
	if (canCreateThread.value) {
		order.push({ css: '.create-btn', clickOnEnter: true })
	}
	order.push({ css: '.close-icon', clickOnEnter: true })
	order.push({ css: '.pronto-input input' })
	order.push({ all: '.tab-item', clickOnEnter: true })
	order.push({
		name: 'thread-list',
		Enter: async () => {
			if (!threadList.value) return
			threadList.value.accessibilityMode = true
			// Reset index so focusMe takes the focusFirstItem path,
			// which re-queries the DOM via setTabList before focusing
			threadList.value.currentIndex = -1
			await threadList.value.setTabList(threadListTabOrder.value)
			threadList.value.focusMe()
		},
	})
	return order
})

const threadListTabOrder = computed(() => {
	// Depend on loading/threads so tabOrder recomputes after async fetch,
	// causing Accessible to re-query DOM for row elements
	const _threadCount = loading.value ? 0 : threads.value.length
	return [
		{
			all: '.table-row, .empty-create-btn',
			clickOnEnter: true,
			arrows: true,
			// Forces deep-equality check to fail when threads load,
			// triggering setTabList to re-query the DOM
			_threadCount,
		},
	]
})

const loadThreads = async () => {
	if (!props.chatId) return

	const tab = activeTab.value
	const state = tabData[tab]
	loading.value = true
	try {
		const params = {}
		if (searchQuery.value) {
			params['filter[search]'] = searchQuery.value
		}
		const fetchFn =
			!searchQuery.value && tab === JOINED_TAB
				? fetchMyJoinedThreads
				: fetchThreadsForChat
		const resp = await fetchFn(props.chatId, params)
		state.apiResponse = resp
		state.threads = resp.data || []
	} catch (e) {
		console.error('Failed to load threads:', e)
		state.apiResponse = null
		state.threads = []
	} finally {
		loading.value = false
	}
}

const loadMore = async () => {
	const state = tabData[activeTab.value]
	if (!state.apiResponse?.next) return
	try {
		loadingMore.value = true
		state.apiResponse = await state.apiResponse.next()
		state.threads = state.threads.concat(state.apiResponse.data || [])
	} catch (e) {
		console.error('Failed to load more threads:', e)
	} finally {
		loadingMore.value = false
	}
}

const debouncedLoadThreads = debounce(loadThreads, 250)

const onTabChange = (tab) => {
	activeTab.value = tab.id
	loadThreads()
}

const switchToAllTab = () => {
	activeTab.value = ALL_TAB
	loadThreads()
}

const openThread = (thread) => {
	emit('open-thread', thread)
}

const close = () => {
	emit('close')
}

const onDocumentClick = (e) => {
	const el = panel.value?.$el || panel.value
	if (!el) return
	if (!document.contains(e.target)) return
	if (!el.contains(e.target)) {
		close()
	}
}

watch(
	() => props.isOpen,
	(isOpen) => {
		if (isOpen) {
			loadThreads()
			escapeId.value = addEscapeListener(close)
			setTimeout(() => {
				document.addEventListener('click', onDocumentClick)
			})
		} else {
			debouncedLoadThreads.clear()
			searchQuery.value = ''
			activeTab.value = ALL_TAB
			tabData[ALL_TAB] = makeTabState()
			tabData[JOINED_TAB] = makeTabState()
			if (escapeId.value) {
				removeEscapeListener(escapeId.value)
			}
			document.removeEventListener('click', onDocumentClick)
		}
	}
)

watch(
	() => props.chatId,
	() => {
		if (props.isOpen) {
			loadThreads()
		}
	}
)

watch(searchQuery, () => {
	if (!props.isOpen) return
	loading.value = true
	debouncedLoadThreads()
})

onUnmounted(() => {
	if (escapeId.value) {
		removeEscapeListener(escapeId.value)
	}
	document.removeEventListener('click', onDocumentClick)
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.thread-browser {
	position: absolute;
	top: 60px;
	right: 12px;
	width: 600px;
	max-width: calc(100vw - 24px);
	height: 425px;
	background: var(--neutral-0);
	border-radius: 8px;
	box-shadow: 0 4px 40px rgba(0, 0, 0, 0.45);
	z-index: 100;
	@include flex-column;
	overflow: hidden;

	.thread-list-accessible {
		flex: 1;
		min-height: 0;
		@include flex-column;
	}

	.thread-browser-header {
		@include flex-spread;
		padding: 16px;
		position: relative;

		.thread-browser-title {
			font-size: 22px;
			@include fontFamily(900);
		}

		.header-actions {
			@include flex(12px);
			align-items: center;

			.header-divider {
				width: 1px;
				height: 30px;
				background: var(--neutral-200);
			}
		}

		.close-icon {
			font-size: 24px;
			color: var(--neutral-500);
			&:hover {
				color: var(--neutral-700);
			}
		}
	}

	.thread-search-wrapper {
		padding: 0px 16px 6px 16px;
	}

	.thread-browser-empty {
		@include flex-column-center;
		flex: 1;
		padding: 0px 16px;
		text-align: center;
		justify-content: center;

		.empty-icon {
			font-size: 48px;
			color: var(--blue-300);
			margin-bottom: 7px;
			transform: rotate(30deg);
		}

		.empty-title {
			font-size: 18px;
			font-family: Avenir-Heavy;
			color: var(--neutral-800);
			margin-bottom: 8px;
		}

		.empty-description {
			font-size: 14px;
			font-family: Avenir-Roman;
			color: var(--neutral-800);
			max-width: 320px;
			margin-bottom: 20px;
		}

		.empty-create-btn {
			font-size: 14px;
			padding: 10px 24px;
			font-family: Avenir-Black;
		}
	}

	.thread-item {
		@include flex-column;
		gap: 3px;
		width: 100%;
		background: rgba(250, 250, 250, 1);
		border: 1px solid var(--neutral-200);
		border-radius: 6px;
		padding: 10px 12px;

		.thread-title {
			font-size: 15px;
			font-family: Avenir-Heavy;
			color: var(--neutral-800);
			@include ellipsis;
		}

		.thread-preview {
			@include flex(6px);
			align-items: center;
			font-size: 13px;
			color: var(--neutral-800);
			min-width: 0;

			&.no-messages {
				color: var(--neutral-400);
				font-family: Avenir-Roman;
			}

			:deep(.profile-pic-avatar) {
				flex-shrink: 0;
			}

			.preview-sender {
				font-family: Avenir-Heavy;
				color: var(--neutral-800);
				flex-shrink: 0;
			}

			.preview-text {
				@include ellipsis;
				font-family: Avenir-Roman;
				min-width: 0;
			}

			.preview-time {
				flex-shrink: 0;
				color: var(--neutral-400);
				font-size: 12px;

				.time-dot {
					margin-right: 7px;
					font-size: 8px;
				}
			}
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.thread-browser {
	.no-search-results {
		font-size: 18px;
		.pronto-icon {
			font-size: 40px;
		}
	}

	.create-btn {
		font-size: 14px !important;
		font-family: Avenir-Black !important;
	}

	.pronto-table {
		flex: 1;
		min-height: 0;
		padding-top: 6px;

		.table-body {
			background: transparent;
			height: 100%;
		}

		.table-row {
			border-bottom: none;
			padding: 0 8px;

			&:hover:not(.table-header) {
				background: transparent !important;

				.thread-item {
					background: var(--blue-100);
					border-color: var(--blue-200);
				}
			}

			.table-cell {
				padding: 6px 8px;
			}
		}

		.table-empty-state {
			position: static;
			padding: 32px 16px;
		}
	}

	.tabs-wrapper {
		position: relative;
		flex-shrink: 0;

		&::after {
			content: '';
			position: absolute;
			bottom: 0;
			left: 16px;
			right: 16px;
			height: 1px;
			background: var(--neutral-200);
		}

		.tabs-container {
			border-bottom: none;

			.tabs {
				justify-content: flex-start;
				gap: 8px;
				padding: 0 16px;

				.tab-item {
					width: auto;
					padding: 0 12px;
					height: 46px;
					font-family: Avenir-Roman;
					color: var(--neutral-600);

					&.selected {
						font-family: Avenir-Heavy;
						color: var(--blue-300);
					}

					&:hover:not(.selected) {
						color: var(--neutral-700);
					}
				}
			}
		}
	}
}

@include overrideScheme('dark') {
	.thread-browser {
		background: var(--neutral-0) !important;
		border: 1px solid var(--neutral-300);

		.table-row {
			border-bottom: none;
		}

		.thread-search-wrapper {
			background: var(--neutral-0);
		}

		.pronto-input {
			background: var(--neutral-200) !important;
		}

		.thread-item {
			background: var(--neutral-200) !important;

			.thread-title,
			.preview-sender,
			.preview-text {
				color: var(--neutral-800);
			}
		}

		.pronto-table {
			.table-body {
				background: transparent;
			}

			.table-row:hover {
				background: transparent;

				.thread-item {
					background: var(--neutral-300) !important;
					border-color: var(--neutral-300);
				}
			}
		}
	}
}
</style>
